// Shared visual vocabulary for button-like controls. Both <Btn> (a real
// <button>) and <LinkBtn> (a react-router <Link> styled as a button) consume
// these, so a Btn and a LinkBtn of the same size/variant render pixel-identical
// and can never drift apart again.

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning' | 'success' | 'info';
export type ButtonSize = 'sm' | 'md';

// `no-underline` is inert on a <button> but keeps <LinkBtn> from inheriting the
// default anchor underline.
export const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 font-semibold tracking-wide transition no-underline';

export const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-md',
  md: 'px-5 py-2.5 text-sm rounded-md',
};

// `disabled:` utilities are inert on an <a>, harmless to share with <LinkBtn>.
export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'text-white shadow-sm hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed',
  secondary:
    'bg-white border border-slate-200 text-slate-800 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed',
  ghost:
    'bg-transparent text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed',
  warning:
    'bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed',
  success:
    'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed',
  info: 'bg-[#4D148C] text-white hover:bg-[#3B0F6B] disabled:opacity-50 disabled:cursor-not-allowed',
};

// Primary buttons take their fill from the brand accent token. Callers can still
// override with an explicit `style` prop (e.g. the dashboard CTA), which spreads
// after this and therefore wins.
export const PRIMARY_BG = 'var(--brand-accent)';
